
import { useState, useEffect } from 'react';
import { Clock, Check, Calendar, LogIn, LogOut, Timer, History, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { SwitchWithLabel } from '../../components/ui/switch-with-label';
import { formatDistanceToNow } from 'date-fns';

// Dummy punch history data
const punchHistoryData = [
  { date: '2025-04-26', punchIn: '09:15 AM', punchOut: '06:30 PM', totalHours: '9h 15m' },
  { date: '2025-04-25', punchIn: '09:05 AM', punchOut: '05:45 PM', totalHours: '8h 40m' },
  { date: '2025-04-24', punchIn: '09:30 AM', punchOut: '06:15 PM', totalHours: '8h 45m' },
  { date: '2025-04-23', punchIn: '08:55 AM', punchOut: '06:05 PM', totalHours: '9h 10m' },
];

const Punch = () => {
  const { toast } = useToast();
  const [isPunchedIn, setIsPunchedIn] = useState(false);
  const [punchTime, setPunchTime] = useState<string | null>(null);
  const [punchInTime, setPunchInTime] = useState<Date | null>(null);
  const [totalHoursToday, setTotalHoursToday] = useState<string>('0 hours');
  const [showHistory, setShowHistory] = useState(false);
  
  // Calculate elapsed time when punched in
  useEffect(() => {
    let interval: number | undefined;
    
    if (isPunchedIn && punchInTime) {
      interval = window.setInterval(() => {
        const elapsed = formatDistanceToNow(punchInTime);
        setTotalHoursToday(elapsed);
      }, 60000); // Update every minute
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPunchedIn, punchInTime]);
  
  const handlePunch = () => {
    const now = new Date();
    const timeString = now.toLocaleTimeString();
    setPunchTime(timeString);
    
    if (!isPunchedIn) {
      // Punch in
      setPunchInTime(now);
      setTotalHoursToday('Just started');
    } else {
      // Punch out calculation
      if (punchInTime) {
        const diff = now.getTime() - punchInTime.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setTotalHoursToday(`${hours} hours ${minutes} min`);
      }
      setPunchInTime(null);
    }
    
    setIsPunchedIn(!isPunchedIn);
    
    toast({
      title: isPunchedIn ? "Punched Out" : "Punched In",
      description: `You have successfully ${isPunchedIn ? "punched out" : "punched in"} at ${timeString}.`,
      className: "bg-bytesplash-blue text-white",
    });
  };
  
  const toggleHistory = () => {
    setShowHistory(!showHistory);
  };
  
  return (
    <div className="p-6 flex flex-col items-center justify-center min-h-[80vh]">
      <Card className="w-full max-w-md shadow-lg border-t-4 border-t-bytesplash-blue">
        <CardHeader className="text-center border-b pb-4">
          <div className="bg-bytesplash-soft-blue p-4 rounded-full inline-block mx-auto mb-4">
            <Clock className="w-16 h-16 text-bytesplash-blue" />
          </div>
          <CardTitle className="text-2xl font-bold text-bytesplash-blue">Attendance Punch System</CardTitle>
          <p className="text-gray-500 mt-1">Record your attendance by punching in or out</p>
        </CardHeader>
        
        <CardContent className="p-8 flex flex-col items-center text-center space-y-6">
          <div className="w-full">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-2">
                <Calendar className="text-bytesplash-blue w-5 h-5" />
                <span className="text-gray-700 font-medium">Today's Date</span>
              </div>
              <span className="font-semibold text-bytesplash-blue">
                {new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
          </div>
          
          <div className="w-full bg-gray-50 rounded-lg p-4 border border-gray-200 flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <User className="text-bytesplash-blue w-5 h-5" />
                <span className="text-gray-600">Current Status:</span>
              </div>
              <span className={`font-semibold ${isPunchedIn ? "text-green-600" : "text-red-600"}`}>
                {isPunchedIn ? "Working" : "Not Working"}
              </span>
            </div>
            
            {punchTime && (
              <>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Timer className="text-bytesplash-blue w-5 h-5" />
                    <span className="text-gray-600">Last Punch:</span>
                  </div>
                  <span className="font-medium">{punchTime}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Clock className="text-bytesplash-blue w-5 h-5" />
                    <span className="text-gray-600">Today's Hours:</span>
                  </div>
                  <span className="font-medium">{totalHoursToday}</span>
                </div>
              </>
            )}
          </div>
          
          <Button
            className={`w-full text-lg py-6 transition-all duration-200 ${
              isPunchedIn 
                ? "bg-destructive hover:bg-red-700 shadow-md" 
                : "bg-bytesplash-blue hover:bg-blue-700 shadow-md"
            }`}
            onClick={handlePunch}
          >
            {isPunchedIn ? (
              <><LogOut className="mr-2" /> Punch Out</>
            ) : (
              <><LogIn className="mr-2" /> Punch In</>
            )}
          </Button>
          
          {isPunchedIn && (
            <div className="flex items-center text-green-600 animate-fade-in">
              <Check className="w-5 h-5 mr-1" />
              <span>Successfully punched in</span>
            </div>
          )}
        </CardContent>
        
        <CardFooter className="flex flex-col p-6 pt-0 border-t mt-4">
          <div className="w-full">
            <Button 
              variant="ghost" 
              className="flex items-center w-full justify-between text-bytesplash-blue hover:bg-bytesplash-soft-blue mb-2"
              onClick={toggleHistory}
            >
              <span className="font-semibold">Recent Punch History</span>
              <History className="w-5 h-5" />
            </Button>
            
            {showHistory && (
              <div className="mt-3 space-y-2 animate-fade-in">
                {punchHistoryData.map((entry, index) => (
                  <div 
                    key={index} 
                    className="flex justify-between items-center bg-gray-50 p-3 rounded-md border border-gray-100 hover:bg-gray-100 transition-colors"
                  >
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900">{entry.date}</div>
                      <div className="text-xs text-gray-500">
                        {entry.punchIn} - {entry.punchOut}
                      </div>
                    </div>
                    <div className="text-bytesplash-blue font-medium">
                      {entry.totalHours}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardFooter>
      </Card>
      
      <Card className="w-full max-w-md shadow-lg mt-6 bg-bytesplash-soft-blue">
        <CardContent className="p-6 text-center">
          <div className="flex justify-between text-sm text-bytesplash-blue">
            <div>
              <p className="font-medium">Weekly Goal</p>
              <p className="text-xl font-bold mt-1">40 hours</p>
            </div>
            <div>
              <p className="font-medium">Current Week</p>
              <p className="text-xl font-bold mt-1">26.5 hours</p>
            </div>
            <div>
              <p className="font-medium">Remaining</p>
              <p className="text-xl font-bold mt-1">13.5 hours</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Punch;
